import { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Stack, } from '@mui/material';
import Swal from 'sweetalert2';

import NoteAddIcon from '@mui/icons-material/NoteAdd';

import CreateList from "../components/manage_list/CreateList";
import ListViewer from '../components/manage_list/ListViewer';

import FolderListTabs from '../components/manage_list/FolderListTabs';
import FolderCard from '../components/manage_list/FolderCard';

import CustomSearch from '../components/custom_components/CustomSearch';
import CustomButton from '../components/custom_components/CustomButton';
import CustomRefreshButton from '../components/custom_components/CustomRefreshButton';
import CustomSelect from '../components/custom_components/CustomSelect';

import SearchNotFound from '../utils/SearchNotFound';
import ListCardSkeleton from '../utils/ListCardSkeleton';
import { useLockBodyScroll } from '../components/utils';

const ManageList = () => {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [orderList, setOrderList] = useState([]);
    const [openList, setOpenList] = useState(false);
    const [listInfo, setListInfo] = useState([]);

    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const [month, setMonth] = useState("");
    const [year, setYear] = useState(new Date().getFullYear());

    const [openCreateList, setOpenCreateList] = useState(false);

    useLockBodyScroll(openCreateList);

    const startYear = 2025;
    const endYear = new Date().getFullYear() + 5;
    const years = [];

    for (let y = startYear; y <= endYear; y++) {
        years.push(y);
    }

    const [tabValue, setTabValue] = useState(0);

    /* FILTERS */
    const newOrders = orderList.filter(
        (o) => o.Remarks === "New"
    );

    const forUpdating = orderList.filter(
        (o) => o.Remarks === "Update"
    );

    const forMarking = orderList.filter(
        (o) => o.Remarks === "Marking"
    );

    const completedOrders = orderList.filter(
        (o) => o.Remarks === "Completed"
    );

    /* TABS */
    const tabs = [
        {
            label: "New",
            data: newOrders,
            color: "#1976d2",
        },
        {
            label: "For Update",
            data: forUpdating,
            color: "#ed6c02",
        },
        {
            label: "For Marking",
            data: forMarking,
            color: "#0288d1",
        },
        {
            label: "Completed",
            data: completedOrders,
            color: "#2e7d32",
        },
    ];

    // FUNCTION DISPLAY LIST'S 
    const handleDisplayOrderList = useCallback(async (lmonth, lyear) => {

        setLoading(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_list/get_list.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        search: search,
                        month: lmonth,
                        year: lyear,
                    })
                });

            const result = await response.json();

            if (result.status === 'success') {

                setOrderList(result.data || []);

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message,
                    icon: 'error'
                });
            }

        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });
            console.error('Server error:', error);
        } finally {
            setLoading(false);
        }

    }, [debouncedSearch]);

    useEffect(() => {
        handleDisplayOrderList(month, year);
    }, [month, year, handleDisplayOrderList]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);

        return () => clearTimeout(timer);
    }, [search]);

    const handleOpenList = (listInfo) => {
        setListInfo(listInfo);
        setOpenList(true);
    };

    return (
        <Box>

            {openList ?

                <ListViewer onBack={() => setOpenList(false)} listInfo={listInfo} />

                : // 

                <Stack>

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ width: '100%' }}
                        mb={2}
                    >

                        <Stack direction="row" justifyContent="center" gap={2}>

                            <CustomSearch
                                placeholder='Search by List Name'
                                md='36ch'
                                value={search}
                                onChange={(val) => setSearch(val)}
                            />

                            <CustomRefreshButton
                                label="Refresh"
                                loading={loading}
                                onClick={() => handleDisplayOrderList(month, year)}
                            />

                            <CustomSelect
                                label="Filter by Month"
                                minWidth={160}
                                value={month}
                                onChange={(e) => setMonth(e)}
                                disabled={loading}
                                options={[
                                    { label: <em>All</em>, value: "" },
                                    { label: "January", value: "01" },
                                    { label: "February", value: "02" },
                                    { label: "March", value: "03" },
                                    { label: "April", value: "04" },
                                    { label: "May", value: "05" },
                                    { label: "June", value: "06" },
                                    { label: "July", value: "07" },
                                    { label: "August", value: "08" },
                                    { label: "September", value: "09" },
                                    { label: "October", value: "10" },
                                    { label: "November", value: "11" },
                                    { label: "December", value: "12" },
                                ]}
                            />

                            <CustomSelect
                                label="Filter by Year"
                                minWidth={160}
                                value={year}
                                onChange={(e) => setYear(e)}
                                disabled={loading}
                                options={[
                                    { label: <em>All</em>, value: "" },
                                    ...years.map((y) => ({
                                        label: y,
                                        value: y,
                                    })),
                                ]}
                            />

                        </Stack>

                        <CustomButton
                            label="Create List"
                            startIcon={<NoteAddIcon />}
                            onClick={() => setOpenCreateList(true)}
                        />
                    </Stack>

                    <FolderListTabs
                        tabValue={tabValue}
                        setTabValue={setTabValue}
                        tabs={tabs}
                    />

                    {/* CONTENT */}
                    {loading ? (

                        <Grid container spacing={2}>
                            {[...Array(6)].map((_, index) => (
                                <Grid
                                    key={index}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                        lg: 3,
                                    }}
                                >
                                    <ListCardSkeleton />
                                </Grid>
                            ))}
                        </Grid>

                    ) : tabs[tabValue].data.length === 0 ? (

                        <SearchNotFound />

                    ) : (

                        <Grid container spacing={2}>

                            {tabs[tabValue].data.map((list) => (

                                <Grid
                                    key={list.ListID}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                        lg: 3,
                                    }}
                                >

                                    <FolderCard
                                        list={list}
                                        orderList={orderList}
                                        setOrderList={setOrderList}

                                        openList={handleOpenList}
                                    />

                                </Grid>
                            ))}
                        </Grid>
                    )}

                </Stack>
            }

            <CreateList
                open={openCreateList}
                onClose={() => setOpenCreateList(false)}

                onUpdate={(val) =>
                    setOrderList(prev => [
                        val,
                        ...prev
                    ])
                }
            />

        </Box>
    );
};

export default ManageList;

